import type { BranchingStory } from '@/types/storytelling'

export const theMysteriousDoor: BranchingStory = {
  id: 'the-mysterious-door',
  title: 'The Mysterious Door',
  description: 'Shape a story through your choices',
  startNodeId: 'start',
  nodes: {
    start: {
      id: 'start',
      content:
        'You\'re walking home from school when you notice something you\'ve never seen before — a small blue door in the brick wall of the old library. It\'s barely three feet tall. There\'s no handle, just a keyhole shaped like a star.',
      choices: [
        { id: 'knock', text: 'Knock on the door', preview: 'Maybe someone — or something — is on the other side.' },
        { id: 'peek', text: 'Peek through the keyhole', preview: 'See what\'s inside before committing.' },
        { id: 'walk-away', text: 'Keep walking', preview: 'Some doors aren\'t meant to be opened.' },
      ],
    },
    knock: {
      id: 'knock',
      content:
        'You knock three times. The sound is deeper than it should be — like knocking on something much bigger than a small door. Silence. Then, from behind the door, a voice: "Finally. We\'ve been waiting."',
      choices: [
        { id: 'ask-who', text: '"Who\'s we?"', preview: 'Get answers before going further.' },
        { id: 'step-back', text: 'Step back from the door', preview: 'Doors that talk back are usually trouble.' },
      ],
    },
    peek: {
      id: 'peek',
      content:
        'You crouch down and press your eye to the star-shaped keyhole. Through it, you see — impossible — a vast forest, with trees taller than any building you\'ve ever seen. Snow is falling, but it\'s golden, not white. And something is moving between the trees.',
      choices: [
        { id: 'try-open', text: 'Try to push the door open', preview: 'A world like that is worth exploring.' },
        { id: 'tell-friend', text: 'Run to get your friend', preview: 'You need a witness for this.' },
      ],
    },
    'walk-away': {
      id: 'walk-away',
      content:
        'You keep walking. Twelve steps later, you stop. You can feel the door behind you, like it\'s watching. You\'ve passed this wall a thousand times and never once seen a door. Tomorrow, will it still be there? You turn around. The door is gone. Just bricks. Just wall. But in your pocket — you don\'t remember putting it there — is a small brass key shaped like a star.',
      isEnding: true,
      endingType: 'twist',
    },
    'ask-who': {
      id: 'ask-who',
      content:
        'There\'s a pause. Then the voice again — but different this time, younger: "Everyone who ever started a story and didn\'t finish it. They\'re all here. And they need someone to write the ending." The door clicks. It swings open an inch. Warm light spills through the crack.',
      choices: [
        { id: 'enter', text: 'Step through', preview: 'Stories need endings. You can help.' },
        { id: 'close-it', text: 'Push the door shut', preview: 'That\'s a lot of responsibility.' },
      ],
    },
    'step-back': {
      id: 'step-back',
      content:
        'You step back. Smart move. The voice sighs — not angry, just tired. "That\'s okay. The door will be here when you\'re ready." When you blink, the door is just a door again. Small. Blue. Silent. But you know now: it\'s real. And it\'s waiting.',
      isEnding: true,
      endingType: 'neutral',
    },
    'try-open': {
      id: 'try-open',
      content:
        'You push, but the door doesn\'t budge. Then you notice: the star-shaped keyhole is glowing faintly. You don\'t have a key. But as you pull your hand back, something tumbles out of your sleeve — a brass key shaped like a star. You don\'t remember picking it up. You don\'t remember it being there. But it fits perfectly.',
      choices: [
        { id: 'turn-key', text: 'Turn the key', preview: 'The forest is waiting.' },
        { id: 'pocket-key', text: 'Pocket the key and walk away', preview: 'Not today. But now you have the key.' },
      ],
    },
    'tell-friend': {
      id: 'tell-friend',
      content:
        'You sprint to the corner where your friend Noor always waits. "Come look at this door!" Noor follows you back. But when you get there — just bricks. Just wall. Noor gives you a look. "What door?" You touch the wall. It\'s warm. Noor doesn\'t notice. In your pocket, something you didn\'t put there: a star-shaped key.',
      isEnding: true,
      endingType: 'twist',
    },
    enter: {
      id: 'enter',
      content:
        'You step through. The door closes softly behind you. You\'re standing in a library — infinite, impossible, shelves stretching in every direction. Each book is glowing faintly, and each one is open to the last page. Blank. Every story here is unfinished. And somewhere in the distance, you hear a pen scratching. Someone is already writing. You take a breath and walk toward the sound.',
      isEnding: true,
      endingType: 'good',
    },
    'close-it': {
      id: 'close-it',
      content:
        'You push the door shut. It clicks. The warm light vanishes. You stand there in the ordinary afternoon, heart pounding. That was real. All of it was real. And somewhere behind that wall, stories are still waiting for their endings. You put your hands in your pockets and walk home. Your fingers close around something cold and metal. A star-shaped key.',
      isEnding: true,
      endingType: 'neutral',
    },
    'turn-key': {
      id: 'turn-key',
      content:
        'The key turns. The lock sings — a single clear note, like a bell. The door swings open. Golden snow drifts toward you. The forest smells like old books and woodsmoke. Between the trees, the shape you saw is closer now: a fox, silver-furred, watching you with calm, ancient eyes. It turns and walks deeper into the forest, looking back once — an invitation. You step through.',
      isEnding: true,
      endingType: 'good',
    },
    'pocket-key': {
      id: 'pocket-key',
      content:
        'You put the key in your pocket. Not today. The forest and the golden snow and whatever lives between those trees — they can wait. But the weight of the key in your pocket feels like a promise. Tomorrow, you\'ll come back. Or the next day. The door will be here. You\'re sure of it.',
      isEnding: true,
      endingType: 'neutral',
    },
  },
}
